/* Varianten-Waehler in Produktkarten (snippets/card-variant-picker.liquid) */
const IMAGE_WIDTHS = [180, 360, 540, 720, 900, 1080];
const SLIDE_DURATION = 450;
const SLIDE_EASING = 'cubic-bezier(.7, 0, .3, 1)';

class CardVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    // Script laedt async (nicht erst nach der ganzen Seite) -> eine Karte kann schon verbunden sein, waehrend
    // ihre Variantendaten noch geparst werden. Dann kurz spaeter erneut versuchen.
    let variants = null;
    try {
      variants = JSON.parse(this.querySelector('script[data-cvp-variants]')?.textContent || 'null');
    } catch (error) {
      variants = null;
    }
    if (!Array.isArray(variants)) {
      if (!this.retry) {
        this.retry = setTimeout(() => {
          this.retry = null;
          if (this.isConnected) this.connectedCallback();
        }, 50);
      }
      return;
    }
    this.initialized = true;

    this.variants = variants;
    this.card = this.closest('.product-card');
    // Hover-Warenkorb-Button der Karte (fehlt, wenn die ganze Karte ausverkauft ist)
    this.form = this.card?.querySelector('form.cvp-form');
    this.input = this.form?.querySelector('[name="id"]');
    this.button = this.form?.querySelector('[type="submit"]');
    this.variantTitle = this.card?.querySelector('[data-cvp-variant-title]');

    const pressed = [...this.querySelectorAll('[data-cvp-index]')].map((row) => [
      Number(row.dataset.cvpIndex),
      row.querySelector('[aria-pressed="true"]')?.dataset.cvpValue,
    ]);
    const start =
      (this.input && this.variants.find((v) => String(v.id) === this.input.value)) ||
      this.variants.find((v) => pressed.every(([i, value]) => value === undefined || v.o[i] === value)) ||
      this.variants[0];
    this.selection = start.o.slice();

    // Klick irgendwo auf die Karte (ausser Buttons/Links) oeffnet das Produkt mit der gewaehlten Variante
    if (this.card && !this.card.dataset.cvpClickable) {
      this.card.dataset.cvpClickable = 'true';
      this.card.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.target.closest('a, button, input, select, textarea, label, form, quick-view, [role="dialog"]')) return;
        if (window.getSelection && String(window.getSelection())) return;
        const url = this.current ? this.current.u : null;
        if (!url) return;
        if (event.metaKey || event.ctrlKey) {
          window.open(url, '_blank');
        } else {
          window.location.href = url;
        }
      });
    }

    this.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-cvp-value]');
      if (!choice) return;
      event.preventDefault();
      event.stopPropagation();
      // per Touch schon beim Loslassen gewaehlt (siehe unten) -> den nachfolgenden Klick ignorieren
      if (this.tapChosen && event.timeStamp - this.tapChosen < 1000) return;
      this.choose(Number(choice.closest('[data-cvp-index]').dataset.cvpIndex), choice.dataset.cvpValue);
    });

    // iPhone: in den ersten Sekunden nach dem Laden kommt der Tipp an, Safari macht aber keinen Klick daraus
    // (User 2026-10-28, Messung: touchstart ja, click erst beim 3. Tipp). Darum bei Touch direkt beim
    // Loslassen waehlen, solange sich der Finger kaum bewegt hat (sonst war es Scrollen).
    let tap = null;
    this.addEventListener('pointerdown', (event) => {
      const choice = event.pointerType === 'touch' && event.target.closest('[data-cvp-value]');
      tap = choice ? { choice, x: event.clientX, y: event.clientY, time: event.timeStamp } : null;
    });
    this.addEventListener('pointercancel', () => {
      tap = null;
    });
    this.addEventListener('pointerup', (event) => {
      if (!tap || event.pointerType !== 'touch') return;
      const { choice, x, y, time } = tap;
      tap = null;
      if (Math.abs(event.clientX - x) > 10 || Math.abs(event.clientY - y) > 10 || event.timeStamp - time > 800) return;
      this.tapChosen = event.timeStamp;
      this.choose(Number(choice.closest('[data-cvp-index]').dataset.cvpIndex), choice.dataset.cvpValue);
    });

    // Farb-Vorschau: Kartenbild wechselt schon beim Ueberfahren mit der Maus
    this.image = this.card?.querySelector('.product-card__media > a.media > img');
    const colorRow = this.querySelector('.cvp__values--swatch')?.closest('[data-cvp-index]');
    this.colorIndex = colorRow ? Number(colorRow.dataset.cvpIndex) : -1;
    this.colorValues = colorRow ? [...colorRow.querySelectorAll('[data-cvp-value]')].map((el) => el.dataset.cvpValue) : [];
    if (this.image) {
      this.originalImage = { src: this.image.getAttribute('src'), srcset: this.image.getAttribute('srcset') };
      this.shownKey = 'original';
      this.shownPosition = this.position(start);
      this.querySelectorAll('.cvp__swatch[data-cvp-value]').forEach((swatch) => {
        const index = Number(swatch.closest('[data-cvp-index]').dataset.cvpIndex);
        swatch.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          const preview = this.closestVariant(index, swatch.dataset.cvpValue);
          if (preview && preview.m) this.showImage(preview.m, this.position(preview));
        });
        swatch.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.restoreImage();
        });
      });
    }

    this.update(start, false);
  }

  find(selection) {
    return this.variants.find((v) => v.o.every((value, i) => value === selection[i]));
  }

  // Variante zur aktuellen Auswahl mit geaendertem Wert; gibt es die Kombination nicht:
  // aehnlichste Variante mit diesem Wert, verfuegbare bevorzugt
  closestVariant(index, value) {
    const wanted = this.selection.slice();
    wanted[index] = value;
    const exact = this.find(wanted);
    if (exact) return exact;
    const score = (v) => (v.a ? 100 : 0) + v.o.reduce((sum, o, i) => sum + (o === wanted[i] ? 10 - i : 0), 0);
    return this.variants.filter((v) => v.o[index] === value).sort((a, b) => score(b) - score(a))[0];
  }

  // Reihenfolge der Farbe im Waehler: bestimmt die Wischrichtung
  position(variant) {
    if (!variant || this.colorIndex < 0) return 0;
    return Math.max(0, this.colorValues.indexOf(variant.o[this.colorIndex]));
  }

  showImage(url, position) {
    if (!this.image || !url) return;
    const base = url + (url.includes('?') ? '&' : '?') + 'width=';
    this.swapImage(
      { src: `${base}720`, srcset: IMAGE_WIDTHS.map((w) => `${base}${w} ${w}w`).join(', ') },
      url,
      position
    );
  }

  restoreImage() {
    if (!this.image) return;
    const position = this.position(this.current);
    if (this.changed && this.current && this.current.m) {
      this.showImage(this.current.m, position);
    } else if (this.originalImage) {
      this.swapImage(this.originalImage, 'original', position);
    }
  }

  // Altes Bild wischt hinaus, neues wischt herein (spaetere Farbe von rechts, fruehere von links)
  async swapImage(source, key, position) {
    if (key === this.shownKey) return;
    const token = (this.swapToken = (this.swapToken || 0) + 1);
    const direction = position < this.shownPosition ? -1 : 1;
    this.shownKey = key;
    this.shownPosition = position;

    const next = this.image.cloneNode(false);
    next.removeAttribute('loading');
    if (source.srcset) next.setAttribute('srcset', source.srcset);
    if (source.src) next.setAttribute('src', source.src);
    try {
      await next.decode();
    } catch (error) {
      // Bild laedt trotzdem; dann ohne Vorladen tauschen
    }
    if (token !== this.swapToken) return;

    // laufende Animation sofort beenden, damit nur ein altes Bild im Rahmen liegt
    if (this.slide) this.slide.forEach((animation) => animation.finish());

    const previous = this.image;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !previous.isConnected) {
      previous.replaceWith(next);
      this.image = next;
      return;
    }

    next.classList.add('cvp-slide-in');
    previous.after(next);
    this.image = next;
    const options = { duration: SLIDE_DURATION, easing: SLIDE_EASING };
    const outgoing = previous.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-100 * direction}%)` }],
      options
    );
    const incoming = next.animate(
      [{ transform: `translateX(${100 * direction}%)` }, { transform: 'translateX(0)' }],
      options
    );
    this.slide = [outgoing, incoming];
    incoming.finished
      .catch(() => {})
      .then(() => {
        previous.remove();
        next.classList.remove('cvp-slide-in');
        if (this.slide && this.slide[1] === incoming) this.slide = null;
      });
  }

  choose(index, value) {
    const variant = this.closestVariant(index, value);
    if (!variant) return;

    this.selection = variant.o.slice();
    this.update(variant, true);
  }

  // Anzeigetext eines Optionswerts fuer die Variantenzeile (z. B. "25" -> "25 km/h", "XXL (63-64 cm)" bleibt voll)
  label(index, value) {
    const choice = this.querySelector(`[data-cvp-index="${index}"] [data-cvp-value="${CSS.escape(value)}"]`);
    return (choice && choice.dataset.cvpLabel) || value;
  }

  update(variant, fromUser) {
    if (this.variantTitle) {
      this.variantTitle.textContent = variant.o.map((value, i) => this.label(i, value)).join(', ');
    }

    this.querySelectorAll('[data-cvp-index]').forEach((row) => {
      const index = Number(row.dataset.cvpIndex);
      const current = row.querySelector('[data-cvp-current]');
      current.textContent = variant.o[index] + (current.dataset.unit ? ' ' + current.dataset.unit : '');

      row.querySelectorAll('[data-cvp-value]').forEach((choice) => {
        const probe = this.selection.slice();
        probe[index] = choice.dataset.cvpValue;
        // Gibt es die Kombination nicht, zaehlt die Variante, zu der ein Klick springen wuerde
        // (verfuegbare bevorzugt) - so wird z. B. "125 ccm" auch bei gewaehltem "50 ccm" durchgestrichen,
        // wenn keine 125er-Variante lieferbar ist.
        const match = this.find(probe);
        const target = match || this.closestVariant(index, choice.dataset.cvpValue);
        choice.setAttribute('aria-pressed', String(choice.dataset.cvpValue === variant.o[index]));
        choice.classList.toggle('is-missing', !match);
        choice.classList.toggle('is-sold-out', !target || !target.a);
      });
    });

    if (this.form) {
      const state = variant.s || (variant.a ? 'add' : 'notify');
      const label = this.button.dataset[`${state}Label`];
      this.input.value = variant.id;
      this.button.dataset.variantId = variant.id;
      this.button.dataset.state = state;
      this.button.dataset.notifyUrl = `${variant.u}#benachrichtigen`;
      this.form.error = false;
      this.button.removeAttribute('aria-disabled');
      this.button.querySelector('[data-cvp-button-label]').textContent = label;
      this.button.setAttribute('aria-label', label);
    }

    // Lieferstatus neben dem Preis
    const stock = this.card?.querySelector('[data-cvp-stock]');
    if (stock) {
      const state = variant.s || (variant.a ? 'add' : 'notify');
      stock.dataset.state = state;
      stock.querySelector('[data-cvp-stock-label]').textContent = stock.dataset[`${state}Label`];
    }

    this.current = variant;
    if (!fromUser || !this.card) return;
    this.changed = true;

    const price = this.card.querySelector('.cvp-bottom .price, .product-card__details .price');
    if (price) {
      price.classList.toggle('price--on-sale', Boolean(variant.c));
      price.innerHTML =
        `<span class="price__regular whitespace-nowrap">${variant.p}</span>` +
        (variant.c ? `<span class="price__sale inline-flex items-center h-auto relative">${variant.c}</span>` : '');
    }

    if (variant.m) this.showImage(variant.m, this.position(variant));

    this.card.querySelectorAll('a.product-card__title, .product-card__media > a.media').forEach((link) => {
      link.href = variant.u;
    });
    const quickView = this.card.querySelector('quick-view[data-product-url]');
    if (quickView) quickView.dataset.productUrl = variant.u;
  }
}

// Hover-Button im Zustand "notify": statt Warenkorb die Produktseite oeffnen; dort oeffnet
// layout/theme.liquid bei #benachrichtigen den Verfuegbarkeitsalarm der Back-in-Stock-App.
if (!window.cvpNotifyHandler) {
  window.cvpNotifyHandler = true;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.cvp-add[data-state="notify"]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.href = button.dataset.notifyUrl;
    },
    true
  );
}

// Safari misst den Preistext beim ersten Rendern zu schmal (vor Inter/tabular-nums, z. B. 57 statt 64 px)
// und aktualisiert die Breite danach nicht -> der Preis ragt ueber seine Spalte, "inkl. MwSt." klebt daran,
// bis der erste Variantenwechsel den Preis neu schreibt. Deshalb den Preis nach dem Laden der Schriften
// genauso neu schreiben (gleicher Inhalt, nur frisch gesetzt).
if (!window.cvpFontRelayout) {
  window.cvpFontRelayout = true;
  const relayout = () => {
    document.querySelectorAll('.cvp-bottom .price').forEach((price) => {
      price.innerHTML = price.innerHTML;
    });
  };
  if (document.fonts) {
    document.fonts.ready.then(relayout);
    document.fonts.addEventListener('loadingdone', relayout);
  }
  window.addEventListener('load', relayout);
}

if (!customElements.get('card-variant-picker')) {
  customElements.define('card-variant-picker', CardVariantPicker);
}
